import React, { useState } from 'react';
import {
  Button, Checkbox, Select, Banner, FileDrop, Field, Icon,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../services/api.js';
import { Row } from '../next/ui';

/**
 * ImportSectionNext — the Excel import panel in the new design. Same props and server calls as ImportSection:
 * template download, file pick (drag & drop), replace, partial update (file column ⇄ system field), extra form data.
 */
export default function ImportSectionNext({
  label, endpoint, templateEndpoint, description, onImportDone, extraActions, entityType, identifierColumns,
  extraFormData, periodName, uploadDisabled, uploadDisabledReason,
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [partialMode, setPartialMode] = useState(false);
  const [identifierColumn, setIdentifierColumn] = useState('');
  const [fileColumn, setFileColumn] = useState('');
  const [fileHeaders, setFileHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchHeaders = async (f) => {
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/import/preview-headers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFileHeaders(data.headers || []);
    } catch {
      setFileHeaders([]);
    }
  };
  const pick = (f) => {
    setFile(f || null);
    setResult(null);
    setError('');
    setFileColumn('');
    if (f && partialMode) fetchHeaders(f);
  };

  const upload = async () => {
    if (!file) return;
    if (partialMode && !identifierColumn) { setError('יש לבחור שדה מזהה במערכת'); return; }
    if (partialMode && !fileColumn) { setError('יש לבחור עמודה מהקובץ שמתאימה לשדה המזהה'); return; }
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      let url;
      if (partialMode && entityType) {
        fd.append('identifierColumn', identifierColumn);
        fd.append('fileColumn', fileColumn);
        url = `/import/partial-update/${entityType}`;
      } else {
        fd.append('replace', replace ? 'true' : 'false');
        url = endpoint;
      }
      if (extraFormData) Object.entries(extraFormData).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, v); });
      const { data } = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      setFile(null);
      onImportDone?.(data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בייבוא הקובץ');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get(templateEndpoint, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${label}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  return (
    <div className="ck-panel">
      <button type="button" className="ck-panel__head ck-panel__toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ width: '100%' }}>
        <Icon name="upload" size={16} />
        <span className="ck-panel__titles"><b>{`ייבוא ${label}`}</b>{description ? <small>{description}</small> : null}</span>
        <Icon name={open ? 'up' : 'down'} size={15} />
      </button>
      {open ? (
        <div className="ck-panel__body nx-stack">
          {extraFormData?.periodId && periodName ? <Banner tone="info" icon="calendar">{`הנתונים ישויכו למועד: ${periodName}`}</Banner> : null}
          <Row>
            {templateEndpoint ? <Button size="sm" icon="download" onClick={downloadTemplate}>הורדת תבנית</Button> : null}
            <span className="ck-muted" style={{ fontSize: 12.5 }}>Excel ‏(xlsx / xls)</span>
          </Row>
          {file ? (
            <div className="nx-item is-on">
              <Icon name="file" size={18} />
              <div className="nx-item__main"><b>{file.name}</b><small>{`${(file.size / 1024).toFixed(1)} KB`}</small></div>
              <Button size="sm" variant="ghost" icon="x" onClick={() => pick(null)}>הסרה</Button>
            </div>
          ) : (
            <FileDrop accept=".xlsx,.xls" onFile={pick} title="גררו קובץ Excel לכאן, או לחצו לבחירה" icon="upload" />
          )}
          <Checkbox
            checked={replace}
            onChange={(v) => { setReplace(v); if (v) setPartialMode(false); }}
            label="החלף נתונים קיימים (מחיקה ויצירה מחדש)"
          />
          {identifierColumns?.length ? (
            <>
              <Checkbox
                checked={partialMode}
                onChange={(v) => {
                  setPartialMode(v);
                  if (v) { setReplace(false); if (file) fetchHeaders(file); } else { setIdentifierColumn(''); setFileColumn(''); setFileHeaders([]); }
                }}
                label="עדכון חלקי (עדכון עמודות בלבד ללא מחיקה/הוספה)"
              />
              {partialMode ? (
                <div className="nx-item" style={{ background: 'var(--ck-info-soft)' }}>
                  <div className="nx-stack" style={{ width: '100%', gap: 8 }}>
                    <small>בחרו איזו עמודה מהקובץ מתאימה לאיזה שדה במערכת:</small>
                    <Row align="flex-end">
                      <Field label="עמודה בקובץ">
                        <Select value={fileColumn} onChange={setFileColumn} placeholder={file ? 'בחרו…' : 'קודם בחרו קובץ'} disabled={!file || !fileHeaders.length} options={fileHeaders} />
                      </Field>
                      <span style={{ paddingBottom: 8 }}>⇐</span>
                      <Field label="שדה במערכת">
                        <Select value={identifierColumn} onChange={setIdentifierColumn} placeholder="בחרו…" options={identifierColumns.map((c) => ({ value: c.field, label: c.label }))} />
                      </Field>
                    </Row>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
          <Row>
            <Button
              variant="primary"
              icon={partialMode ? 'edit' : 'upload'}
              disabled={!file || loading || (partialMode && (!identifierColumn || !fileColumn)) || !!uploadDisabled}
              onClick={upload}
            >
              {loading ? 'מעבד…' : partialMode ? 'עדכון חלקי' : 'ייבוא'}
            </Button>
            {uploadDisabled && uploadDisabledReason ? <small style={{ color: 'var(--ck-danger)' }}>{uploadDisabledReason}</small> : null}
          </Row>
          {result ? (
            <Banner tone={result.errors?.length ? 'warning' : 'success'} onClose={() => setResult(null)}>
              {result.message}
              {result.errors?.length ? (
                <ul style={{ margin: '6px 0 0', paddingInlineStart: 18, fontSize: 12.5 }}>
                  {result.errors.slice(0, 5).map((e, i) => <li key={i}>{typeof e === 'string' ? e : `שורה ${e.row}: ${e.error}`}</li>)}
                  {result.errors.length > 5 ? <li>{`ועוד ${result.errors.length - 5} שגיאות...`}</li> : null}
                </ul>
              ) : null}
            </Banner>
          ) : null}
          {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}
          {extraActions ? <div>{extraActions}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
